import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AppLauncher.scss";

const appDetails = {
  "key-profile": { group: "keyboard", color: "#efb899", caption: "KEY CONTROL" },
  realtime: { group: "daily", color: "#8ce0ca", caption: "LIVE DISPLAY" },
  settings: { group: "keyboard", color: "#a7bccb" },
  lighting: { group: "keyboard", color: "#f0d596" },
  wallpaper: { group: "daily", color: "#b5a4ef" },
  music: { group: "daily", color: "#efa792" },
  numpad: { group: "keyboard", color: "#b9ce98" },
  calculator: { group: "daily", color: "#dfa6c5" },
  touchpad: { group: "keyboard", color: "#92c3e0" },
  tomato: { group: "daily", color: "#e6bd88" },
  pet: { group: "daily", color: "#a0d5bd" },
};

const iconPaths = {
  "key-profile": <><rect x="3" y="5" width="18" height="14" rx="4" /><path d="M7 9h2m3 0h1m3 0h1M7 13h1m3 0h2m3 0h1M8 16h8" /></>,
  realtime: <><rect x="2" y="4" width="20" height="14" rx="3" /><path d="m5 13 4-4 4 3 5-5M9 22h6m-3-4v4" /></>,
  settings: <><path d="m10 3-.7 2.1-2 .9-2-.4-2 3.5 1.3 1.7v2.4L3.3 15l2 3.5 2-.4 2 .9.7 2h4l.7-2 2-.9 2 .4 2-3.5-1.3-1.8v-2.4l1.3-1.7-2-3.5-2 .4-2-.9L14 3Z" /><circle cx="12" cy="12" r="3" /></>,
  lighting: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.4 1.4m11.2 11.2L19 19M5 19l1.4-1.4M17.6 6.4 19 5" /></>,
  wallpaper: <><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="8.5" cy="9" r="1.5" /><path d="m4 17 5-5 4 4 3-3 4 4" /></>,
  music: <><path d="M10 17V5l10-2v12M10 8l10-2" /><ellipse cx="6.5" cy="17.5" rx="3.5" ry="2.5" /><ellipse cx="16.5" cy="15.5" rx="3.5" ry="2.5" /></>,
  numpad: <><rect x="4" y="3" width="16" height="18" rx="3" /><path d="M8 7h1m6 0h1M8 11h1m6 0h1M8 15h1m6 0h1M8 18h4m4-4v4" /></>,
  calculator: <><rect x="5" y="2.5" width="14" height="19" rx="3" /><path d="M8 6h8M8 11h2m4 0h2M8 15h2m4 0h2M8 18h2m4-2v3" /></>,
  touchpad: <><rect x="3" y="4" width="18" height="16" rx="4" /><path d="M4 15h16m-8 0v4m-4-9h8m-4-3v6" /></>,
  tomato: <><circle cx="12" cy="13" r="8" /><path d="M12 8v5l3 2M9 2h6M12 2v3m6 1 2-2" /></>,
  pet: <><path d="M6.5 16c0-1.9 1.5-2.3 2.7-4.1a3.4 3.4 0 0 1 5.6 0c1.2 1.8 2.7 2.2 2.7 4.1 0 2.5-2.7 3.7-5.5 2.2C9.2 19.7 6.5 18.5 6.5 16Z" /><ellipse cx="5.5" cy="9.5" rx="2" ry="2.6" transform="rotate(-20 5.5 9.5)" /><ellipse cx="10" cy="5.5" rx="1.7" ry="2.5" /><ellipse cx="15" cy="5.5" rx="1.7" ry="2.5" /><ellipse cx="19" cy="9.5" rx="2" ry="2.6" transform="rotate(20 19 9.5)" /></>,
};

export function LauncherGlyph({ id }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{iconPaths[id]}</svg>;
}

// Equal-area points avoid bunching cards together at the poles.
function spherePoint(index, count) {
  const y = 1 - 2 * (index + 0.5) / count;
  const ring = Math.sqrt(1 - y * y);
  const angle = index * Math.PI * (3 - Math.sqrt(5));
  return { x: Math.cos(angle) * ring, y, z: Math.sin(angle) * ring };
}

export default function AppLauncher({ apps, onOpen }) {
  const [group, setGroup] = useState("all");
  const [paused, setPaused] = useState(false);
  const [listView, setListView] = useState(false);
  const stageRef = useRef(null);
  const motion = useRef({ yaw: 0.3, pitch: -0.16, dragging: false, hovered: false, focused: false, suppressClick: false });
  const visibleApps = useMemo(() => apps.filter((app) => group === "all" || (appDetails[app.id] || {}).group === group), [apps, group]);
  const points = useMemo(() => visibleApps.map((app, index) => spherePoint(index, visibleApps.length)), [visibleApps]);

  useEffect(() => {
    if (listView) return undefined;
    const stage = stageRef.current;
    const state = motion.current;
    const cards = Array.from(stage.querySelectorAll(".orbit-card"));
    const fragments = Array.from(stage.querySelectorAll(".orbit-fragment"));
    const media = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    let frame;
    let previous;
    let dirty = true;
    let width = stage.clientWidth;
    let height = stage.clientHeight;
    const paint = () => {
      const radius = Math.max(30, Math.min(width - 80, height - 100) / 2);
      const cosY = Math.cos(state.yaw), sinY = Math.sin(state.yaw);
      const cosX = Math.cos(state.pitch), sinX = Math.sin(state.pitch);
      const place = (element, point, decorative) => {
        const x = point.x * cosY + point.z * sinY;
        const z0 = point.z * cosY - point.x * sinY;
        const y = point.y * cosX - z0 * sinX;
        const z = point.y * sinX + z0 * cosX;
        const perspective = 650 / (650 - z * radius);
        const scale = (decorative ? 0.75 : 1) * perspective;
        element.style.transform = `translate(-50%, -50%) translate(${x * radius * perspective}px, ${y * radius * perspective}px) perspective(650px) rotateY(${x * 38}deg) rotateX(${-y * 28}deg) scale(${scale})`;
        element.style.opacity = decorative ? 0.12 + (z + 1) * 0.17 : 0.4 + (z + 1) * 0.3;
        element.style.zIndex = Math.round((z + 1) * 40) + (decorative ? 0 : 2);
      };
      cards.forEach((card, index) => place(card, points[index], false));
      fragments.forEach((card, index) => place(card, spherePoint(index, fragments.length), true));
    };
    state.repaint = () => { dirty = true; };
    const measure = () => { width = stage.clientWidth; height = stage.clientHeight; dirty = true; };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(stage);
    window.addEventListener("resize", measure);
    const tick = (time) => {
      const delta = previous === undefined ? 0 : Math.min(time - previous, 40);
      previous = time;
      if (!document.hidden && !paused && !media?.matches && !state.dragging && !state.hovered && !state.focused) {
        state.yaw += delta * 0.00012;
        dirty = true;
      }
      if (dirty) { paint(); dirty = false; }
      frame = window.requestAnimationFrame(tick);
    };
    paint();
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
      state.repaint = null;
      state.dragging = false;
      state.hovered = false;
      state.focused = false;
    };
  }, [points, paused, listView]);

  const beginDrag = (event) => {
    if (listView || !event.isPrimary || event.button !== 0) return;
    const state = motion.current;
    state.dragging = true;
    state.suppressClick = false;
    state.pointerId = event.pointerId;
    state.startX = state.lastX = event.clientX;
    state.startY = state.lastY = event.clientY;
  };
  const drag = (event) => {
    const state = motion.current;
    if (!state.dragging || event.pointerId !== state.pointerId) return;
    if (Math.hypot(event.clientX - state.startX, event.clientY - state.startY) > 6) {
      state.suppressClick = true;
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.setPointerCapture(event.pointerId);
    }
    if (state.suppressClick) {
      state.yaw += (event.clientX - state.lastX) * 0.008;
      state.pitch = Math.max(-1.1, Math.min(1.1, state.pitch - (event.clientY - state.lastY) * 0.008));
      if (state.repaint) state.repaint();
    }
    state.lastX = event.clientX;
    state.lastY = event.clientY;
  };
  const endDrag = (event) => {
    const state = motion.current;
    if (event.pointerId !== state.pointerId) return;
    state.dragging = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const rotateWithKeys = (event) => {
    if (listView) return;
    const directions = { ArrowLeft: [-0.2, 0], ArrowRight: [0.2, 0], ArrowUp: [0, -0.2], ArrowDown: [0, 0.2] };
    if (!directions[event.key]) return;
    event.preventDefault();
    const state = motion.current;
    state.yaw += directions[event.key][0];
    state.pitch = Math.max(-1.1, Math.min(1.1, state.pitch + directions[event.key][1]));
    if (state.repaint) state.repaint();
  };

  return (
    <div className="launcher-home">
      <header className="launcher-header">
        <div><span className="launcher-eyebrow">ORBIT / YOUR LITTLE SPACE</span><h2>指尖星球 <span>{apps.length} 个应用</span></h2></div>
        <button type="button" className="launcher-settings" aria-label="切换应用视图" aria-pressed={listView} onClick={() => setListView(!listView)}>{listView ? "◉" : "☷"}</button>
      </header>
      <div className="launcher-library-heading">
        <span>{listView ? "应用列表" : "环绕你的每一天"}</span>
        <div className="launcher-filters" role="group" aria-label="应用分类">
          {[{ id: "all", label: "全部" }, { id: "keyboard", label: "键盘" }, { id: "daily", label: "日常" }].map((filter) => <button type="button" key={filter.id} aria-pressed={group === filter.id} className={group === filter.id ? "is-selected" : ""} onClick={() => setGroup(filter.id)}>{filter.label}</button>)}
        </div>
      </div>
      <div ref={stageRef} className={`launcher-orbit ${listView ? "is-list" : ""}`} role="group" aria-label="3D 球形应用，拖动或使用方向键旋转" onPointerDown={beginDrag} onPointerMove={drag} onPointerUp={endDrag} onPointerCancel={endDrag} onLostPointerCapture={() => { motion.current.dragging = false; }} onPointerEnter={(event) => { if (event.pointerType !== "touch") motion.current.hovered = true; }} onPointerLeave={() => { motion.current.hovered = false; motion.current.dragging = false; }} onFocusCapture={() => { motion.current.focused = true; }} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) motion.current.focused = false; }} onKeyDown={rotateWithKeys}>
        {!listView && <div className="orbit-guides" aria-hidden="true"><i /><i /><i /><span>YOUR APPS<br />IN ORBIT</span></div>}
        {!listView && Array.from({ length: 24 }, (_, index) => <span key={`fragment-${index}`} className={`orbit-fragment fragment-${index % 4}`} aria-hidden="true"><i /><i /><i /></span>)}
        {visibleApps.map((app, index) => (
          <button type="button" key={app.id} className={`orbit-card orbit-${app.id}`} aria-label={app.label} style={{ "--app-accent": (appDetails[app.id] || {}).color || "#9adace" }} onClick={(event) => {
            if (event.detail !== 0 && motion.current.suppressClick) { motion.current.suppressClick = false; return; }
            onOpen(app.id);
          }}>
            <span className="orbit-card-topline"><span>{String(index + 1).padStart(2, "0")}</span><span>↗</span></span>
            <span className="orbit-card-art" aria-hidden="true" style={app.id === "wallpaper" ? { backgroundImage: `linear-gradient(#10223a33, #10223a66), url("${process.env.PUBLIC_URL}/wallpapers/wallpaper-photo.jpg")` } : undefined}><LauncherGlyph id={app.id} /><i /><i /></span>
            <strong>{app.label}</strong>
            <small>{(appDetails[app.id] || {}).caption || "LITTLE MOMENTS"}</small>
          </button>
        ))}
      </div>
      <footer className="launcher-orbit-footer">
        <span><i />{listView ? "点击打开应用" : "拖动旋转 · 点击打开"}</span>
        {!listView && <button type="button" aria-label={paused ? "继续旋转" : "暂停旋转"} aria-pressed={paused} onClick={() => setPaused(!paused)}><span aria-hidden="true">{paused ? "▷" : "Ⅱ"}</span>{paused ? "继续" : "暂停"}</button>}
      </footer>
    </div>
  );
}

